import React, {useEffect, useState} from 'react';
import WizardLayout from './layout/WizardLayout';
import ProfessionalExperience from './steps/ProfessionalExperience/ProfessionalExperience';
import Education from './steps/Education/Education';
import Skills from './steps/Skills/Skills';
import {useHistory, useLocation} from 'react-router-dom';
import {COMPLETE_CURRICULUM_VITAE, getErrorUpdateToastPropsBy} from '../../service/toastService';
import {useSelector} from 'react-redux';
import {getCurriculumVitaeByUserId, updateCurriculumVitae} from '../../api/curriculumVitaeApi';
import {toast} from 'react-toastify';
import Language from './steps/Language/Language';
import Reference from './steps/Reference/Reference';
import Certification from './steps/Certification/Certification';
import PersonalInfo from './steps/PersonalInfo/PersonalInfo';
import CvMainMenu from './layout/CvMainMenu';

function Wizard() {
    const location = useLocation();
    const history = useHistory();
    const [curriculumVitae, setCurriculumVitae] = useState(location.state?.cvData || null);
    const currentLoggedUserId = useSelector((state) => state.users.selectedUser.myportalUserId);

    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 7;
    const [stepCompletion, setStepCompletion] = useState({
        1: false, // Personal Info
        2: false, // Professional Experience
        3: false, // Education
        4: false, // Skills
        5: false, // Language
        6: false, // Reference
        7: false, // Certification
    });
    const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true);

    useEffect(() => {
        const fetchCurriculumVitaeData = async () => {
            if (!currentLoggedUserId) return;

            try {
                const response = await getCurriculumVitaeByUserId(currentLoggedUserId);
                setCurriculumVitae(response.data);
                validateSteps(response.data);
            } catch {
                toast.error('Something went wrong! Please try again.', getErrorUpdateToastPropsBy(COMPLETE_CURRICULUM_VITAE));
            }
        };

        fetchCurriculumVitaeData();
    }, [currentLoggedUserId]);

    useEffect(() => {
        if (curriculumVitae) {
            validateSteps(curriculumVitae);
        }
    }, [curriculumVitae]);

    useEffect(() => {
        setIsNextButtonDisabled(!stepCompletion[currentStep]);
    }, [stepCompletion, currentStep]);

    const validateSteps = async (cvData) => {
        const updatedStepCompletion = {
            1: true,
            2: (cvData.curriculumVitaeExperiences || []).length > 0,
            3: (cvData.curriculumVitaeEducations || []).length > 0,
            4: (cvData.curriculumVitaeSkills || []).length > 0,
            5: (cvData.curriculumVitaeLanguages || []).length > 0,
            6: (cvData.curriculumVitaeReferences),
            7: (cvData.curriculumVitaeCertifications),
        };

        if (!updatedStepCompletion[1]) {
            updatedStepCompletion[1] = false;
        }

        for (let step = 2; step <= 7; step++) {
            if (!updatedStepCompletion[step - 1]) {
                updatedStepCompletion[step] = false;
            }
        }

        setStepCompletion(updatedStepCompletion);
    };

    const onNext = async () => {
        if (currentStep < totalSteps) {
            setCurrentStep((prev) => prev + 1);
        } else {
            await updateCurriculumVitae({
                ...curriculumVitae,
                status: 1, // status 1 means COMPLETED
            });
            history.push('/curriculumVitae/end');
        }
    };
    
    const onPrevious = () => {
        setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));
    };

    const handleStepChange = (step) => {
        setCurrentStep(step);
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return <PersonalInfo currentLoggedUserId={currentLoggedUserId} />
            case 2:
                return <ProfessionalExperience curriculumVitae={curriculumVitae} setCurriculumVitae={setCurriculumVitae}/>;
            case 3:
                return <Education curriculumVitae={curriculumVitae} setCurriculumVitae={setCurriculumVitae}/>;
            case 4:
                return <Skills curriculumVitae={curriculumVitae} setCurriculumVitae={setCurriculumVitae}/>;
            case 5:
                return <Language curriculumVitae={curriculumVitae} setCurriculumVitae={setCurriculumVitae}/>;
            case 6:
                return <Reference curriculumVitae={curriculumVitae} setCurriculumVitae={setCurriculumVitae}/>
            case 7:
                return <Certification curriculumVitae={curriculumVitae} setCurriculumVitae={setCurriculumVitae}/>;
            default:
                return <PersonalInfo curriculumVitae={curriculumVitae} setCurriculumVitae={setCurriculumVitae}/>
        }
    };

    return curriculumVitae?.status === 1 ? (
        <CvMainMenu curriculumVitae={curriculumVitae} setCurriculumVitae={setCurriculumVitae} currentLoggedUserId={currentLoggedUserId}/>
    ) : (
        <WizardLayout
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={onNext}
            onPrevious={onPrevious}
            onStepChange={handleStepChange}
            isNextDisabled={isNextButtonDisabled}
            stepCompletion={stepCompletion}
        >
            {renderStep()}
        </WizardLayout>
    );
}

export default Wizard;
